import React, { useState, useMemo, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Common from "../../Common/Common";
import { clusterBaseName, matchingCandidate } from "../../Common/placeNaming";
import PhotoViewer from "./PhotoViewer";
import MapPickDialog from "./MapPickDialog";
import { reverseGeocodeCoords, findIdentifyCandidates, nearestOtherCluster } from "../../Common/placeIdentify";
import IdentifyPlace, { formatDist } from "./IdentifyPlace";
import { isWebPhoto } from "../../Common/photoSource";

const THUMB_W = 90;
const THUMB_H = 80;

function resolveUrl(url, thumb) {
  if (thumb) return thumb;
  if (!url) return null;
  if (url.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${url.slice(7)}`;
  if (url.startsWith("http")) return url;
  return `http://liquidguides.com//Media/Photos/${url}`;
}

function photoName(photo) {
  let n = photo.name || "New Place";
  if (n.toUpperCase().includes(".JPG")) n = n.slice(0, -4);
  return n;
}

// Existing-place row: each candidate photo can independently be marked as
// the cover (★, single-select — replaces Photo/Photo_m) and/or added to
// notes (☑, multi-select — embeds <img> in Info). Unlike the new-place
// Thumb below, clicking the image itself does nothing — the two actions
// are distinct enough that an implicit third meaning for "tap the photo"
// would just be confusing.
function MatchThumb({ src, name, dist, isCover, inNotes, isGuideCover, onToggleCover, onToggleNotes, onToggleGuideCover, onEnlarge }) {
  return (
    <div
      style={{
        borderRadius: 8,
        border: isGuideCover ? "3px solid #f59e0b" : isCover ? "3px solid #16a34a" : inNotes ? "3px solid #2563eb" : "3px solid transparent",
        background: "#f3f4f6",
        overflow: "hidden",
        width: THUMB_W,
        flexShrink: 0,
        position: "relative",
      }}
      title={name}
    >
      {src
        ? <img src={src} alt="" referrerPolicy="no-referrer" style={{ width: THUMB_W, height: THUMB_H, objectFit: "cover", display: "block" }} />
        : <div style={{ width: THUMB_W, height: THUMB_H, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 11 }}>No photo</div>
      }
      {dist != null && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          background: "rgba(0,0,0,0.55)", color: "#fff",
          fontSize: 10, padding: "2px 4px", textAlign: "center",
        }}>
          {formatDist(dist)}
        </div>
      )}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "rgba(0,0,0,0.55)", display: "flex",
      }}>
        {/* Full size (4.10.55) */}
        <button
          className="pjr-enlarge"
          onClick={onEnlarge}
          title="See this photo full size"
          aria-label="See this photo full size"
          style={{ border: "none", background: "none", cursor: "pointer", color: "#fff", fontSize: 11, padding: "3px 5px" }}
        >
          🔍
        </button>
        <button
          onClick={onToggleCover}
          title="Use as cover photo"
          style={{
            flex: 1, border: "none", background: "none", cursor: "pointer",
            color: isCover ? "#4ade80" : "#fff", fontSize: 11, padding: "3px 0",
          }}
        >
          {isCover ? "★" : "☆"}
        </button>
        <button
          onClick={onToggleNotes}
          title="Add to notes"
          style={{
            flex: 1, border: "none", background: "none", cursor: "pointer",
            color: inNotes ? "#60a5fa" : "#fff", fontSize: 11, padding: "3px 0",
          }}
        >
          {inNotes ? "☑" : "☐"}
        </button>
        <button
          onClick={onToggleGuideCover}
          title="Use as guide's cover photo"
          style={{
            flex: 1, border: "none", background: "none", cursor: "pointer",
            color: isGuideCover ? "#f59e0b" : "#fff", fontSize: 11, padding: "3px 0",
          }}
        >
          🏠
        </button>
      </div>
    </div>
  );
}

// New-place row: identical interaction set to PhotoJournalReviewModal's
// Thumb (include/exclude, cover, split-out) — this is deliberately the
// same clustering UI from the "Create Journal from Photos" rebuild, not a
// re-implementation, since a "new place" candidate here is exactly that
// same kind of cluster.
function NewPlaceThumb({ src, name, included, isCover, isGuideCover, onToggleInclude, onSetCover, onSplitOut, onToggleGuideCover, showSplit, onEnlarge }) {
  return (
    <div
      style={{
        borderRadius: 8,
        border: isGuideCover ? "3px solid #f59e0b" : isCover ? "3px solid #16a34a" : included ? "3px solid #2563eb" : "3px solid transparent",
        background: "#f3f4f6",
        overflow: "hidden",
        width: THUMB_W,
        flexShrink: 0,
        position: "relative",
        opacity: included ? 1 : 0.45,
      }}
    >
      <div onClick={onToggleInclude} style={{ cursor: "pointer" }} title={name}>
        {src
          ? <img src={src} alt="" referrerPolicy="no-referrer" style={{ width: THUMB_W, height: THUMB_H, objectFit: "cover", display: "block" }} />
          : <div style={{ width: THUMB_W, height: THUMB_H, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 11 }}>No photo</div>
        }
      </div>
      <button
        onClick={onToggleGuideCover}
        title="Use as guide's cover photo"
        style={{
          position: "absolute", top: 2, right: 2, border: "none", borderRadius: 4,
          background: "rgba(0,0,0,0.55)", cursor: "pointer",
          color: isGuideCover ? "#f59e0b" : "#fff", fontSize: 11, lineHeight: 1, padding: "2px 3px",
        }}
      >
        🏠
      </button>
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "rgba(0,0,0,0.55)", display: "flex",
      }}>
        {/* Full size (4.10.55) */}
        <button
          className="pjr-enlarge"
          onClick={onEnlarge}
          title="See this photo full size"
          aria-label="See this photo full size"
          style={{ border: "none", background: "none", cursor: "pointer", color: "#fff", fontSize: 11, padding: "3px 5px" }}
        >
          🔍
        </button>
        <button
          onClick={onSetCover}
          title="Use as cover photo"
          style={{
            flex: 1, border: "none", background: "none", cursor: "pointer",
            color: isCover ? "#4ade80" : "#fff", fontSize: 11, padding: "3px 0",
          }}
        >
          {isCover ? "★ Cover" : "☆"}
        </button>
        {showSplit && (
          <button
            onClick={onSplitOut}
            title="Split out as new place"
            style={{ border: "none", background: "none", cursor: "pointer", color: "#fff", fontSize: 11, padding: "3px 5px" }}
          >
            ✂
          </button>
        )}
      </div>
    </div>
  );
}

// An existing place's photo as it is now, first in its row (2026-10-28): to
// see which places still show a web photo (labelled as in Identify Places)
// and replace those with the person's own.
const currentPhoto = (place) => place?.Photo_m || place?.Photo || "";

function CurrentThumb({ url, onEnlarge }) {
  return (
    <div className="pgr-current" style={{ borderRadius: 8, border: "3px solid transparent", background: "#f3f4f6", overflow: "hidden", width: THUMB_W, flexShrink: 0, position: "relative", opacity: 0.85 }} title="The place's photo now">
      {url
        ? <img src={resolveUrl(url)} alt="" referrerPolicy="no-referrer" style={{ width: THUMB_W, height: THUMB_H, objectFit: "cover", display: "block" }} />
        : <div style={{ width: THUMB_W, height: THUMB_H, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 11 }}>No photo</div>}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, padding: "2px 4px", textAlign: "center" }}>
        {isWebPhoto(url) ? <span className="pgr-web">Now · Web photo</span> : "Now"}
      </div>
      {url ? (
        <button
          className="pjr-enlarge"
          onClick={onEnlarge}
          title="See this photo full size"
          aria-label="See this photo full size"
          style={{ position: "absolute", bottom: 2, left: 2, border: "none", borderRadius: 4, background: "rgba(0,0,0,0.55)", cursor: "pointer", color: "#fff", fontSize: 11, padding: "2px 4px" }}
        >
          🔍
        </button>
      ) : null}
    </div>
  );
}

// webExtras: places with a web photo but none of the person's photos within
// the match distance (their nearest photos within 1 km, maybe none) — rows
// shown, and applied, only in "Only places whose photo is from the web".
function buildWorkingMatches(matchedItems, webExtras = []) {
  return [...matchedItems, ...webExtras.map((m) => ({ ...m, extra: true }))].map((m) => ({
    place: m.place,
    candidates: m.candidates,
    extra: Boolean(m.extra),
    coverIdx: m.candidates.length > 0 ? 0 : null, // nearest candidate pre-selected, matching Use My Photos' existing default
    notesIncluded: new Set(), // opt-in, matching Add Photos to Places' existing default
  }));
}

function buildWorkingClusters(clusters) {
  return clusters.map((photos) => ({
    photos,
    coverIdx: 0,
    includedIdxs: new Set(photos.map((_, i) => i)),
    enabled: true, // whether this new place is added at all — defaults to add
  }));
}

export default function PersonalizeGuideReviewModal({
  show, onHide, matchedItems, newClusters, sensitivity, onSensitivityChange, maxSpan, onMaxSpanChange,
  minNewPlaceDistance, onMinNewPlaceDistanceChange, unmatchedPlaces, onApply, webExtras = [],
}) {
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [workingMatches, setWorkingMatches] = useState(() => buildWorkingMatches(matchedItems, webExtras));
  const [workingClusters, setWorkingClusters] = useState(() => buildWorkingClusters(newClusters));
  const [baseName, setBaseName] = useState("");
  // "Only add new places" (2026-10-19): existing places keep their photos —
  // their ★/☑ choices (the ★ starts pre-selected) are ignored, so a run can
  // just find new places, as the old "Search Places from Photos" did.
  const [onlyNew, setOnlyNew] = useState(false);
  // "Only places whose photo is from the web" (2026-10-28): a mode for
  // replacing web photos with the person's own — only those places and their
  // nearby photos show; other existing places and all new places are hidden
  // AND left out of Apply.
  const [onlyWebCovers, setOnlyWebCovers] = useState(false);
  // Places ticked "Delete this place" in that mode (never went there).
  const [deleteIds, setDeleteIds] = useState(() => new Set());
  useEffect(() => {
    if (show) {
      setOnlyWebCovers(false);
      setDeleteIds(new Set());
    }
  }, [show]);
  const toggleDelete = (id) =>
    setDeleteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // A tick box above Apply (2026-10-22), not a separate button that was
  // easy to miss: the new places go onto the map as search results.
  const [asResults, setAsResults] = useState(false);
  useEffect(() => { if (show) setAsResults(false); }, [show]);
  // The guide's own cover photo — distinct from a place's ★ cover. Single
  // global selection (there's only one guide cover), tracked by photo
  // object identity since candidates/photos arrays keep stable references
  // through the toggle/split/merge edits below.
  const [guideCoverPhoto, setGuideCoverPhoto] = useState(null);
  const toggleGuideCover = (photo) => setGuideCoverPhoto((prev) => (prev === photo ? null : photo));

  // Runs the exact same dedup PlacesHeader.js's _applyPersonalizeGuide uses,
  // so the preview shown here never promises a name (e.g. "Japan_1") that
  // collides with a place already in the guide — this guide may already
  // have a "Japan_1".."Japan_24" from earlier runs of this same feature.
  const previewNames = useMemo(() => {
    const trimmed = baseName.trim();
    if (!trimmed) return null;
    const used = new Set((window.guide_obj?.places?.placesList || []).map((p) => (p.Name || "").toLowerCase()));
    return workingClusters.map((_, i) => Common.uniquePlaceName(`${trimmed}_${i + 1}`, used));
  }, [baseName, workingClusters]);

  // Per-new-place-cluster "Identify place" results, keyed by cluster index —
  // same opt-in Wikipedia-geosearch/reverse-geocode step as
  // PhotoJournalReviewModal.js, reused here since a "new place" cluster here
  // is exactly that same kind of photo cluster with no better name than
  // "Japan_1" today.
  const [identify, setIdentify] = useState({});
  // "Pick on the map": { ci, lat, lng } of the place being named.
  const [mapPick, setMapPick] = useState(null);
  // A photo shown full size (🔍).
  const [viewerPhoto, setViewerPhoto] = useState(null);

  const [prevMatchedItems, setPrevMatchedItems] = useState(matchedItems);
  const [prevNewClusters, setPrevNewClusters] = useState(newClusters);
  const [prevWebExtras, setPrevWebExtras] = useState(webExtras);
  if (matchedItems !== prevMatchedItems || newClusters !== prevNewClusters || webExtras !== prevWebExtras) {
    setPrevMatchedItems(matchedItems);
    setPrevNewClusters(newClusters);
    setPrevWebExtras(webExtras);
    setWorkingMatches(buildWorkingMatches(matchedItems, webExtras));
    setWorkingClusters(buildWorkingClusters(newClusters));
    setGuideCoverPhoto(null);
    setIdentify({});
  }

  // skipCandidates: see PhotoJournalReviewModal.js's identical function for
  // the full reasoning — bulk identify only ever uses the city, so fetching
  // Wikipedia candidates there was wasted work and the slower of the two
  // parallel calls. candidates: null (not []) distinguishes "never searched"
  // from "searched, found nothing" for IdentifyPlace.js. Found 2026-09-24.
  const runIdentify = async (ci, { skipCandidates = false } = {}) => {
    setIdentify((prev) => ({ ...prev, [ci]: { status: "loading" } }));
    const cluster = workingClusters[ci];
    const cover = cluster.photos[cluster.coverIdx];
    const lat = cover.exif_data.latitude;
    const lng = cover.exif_data.longitude;
    const [{ city }, candidates] = await Promise.all([
      reverseGeocodeCoords(lat, lng),
      skipCandidates ? Promise.resolve(null) : findIdentifyCandidates(lat, lng),
    ]);
    setIdentify((prev) => ({ ...prev, [ci]: { status: "done", city, candidates, pickedTitle: matchingCandidate(currentName(ci), candidates) } }));
  };

  // On-demand landmark search for a cluster bulk identify already resolved a
  // city for but skipped candidates on — reuses the already-known lat/lng.
  const findCandidatesOnly = async (ci) => {
    setIdentify((prev) => ({ ...prev, [ci]: { ...prev[ci], status: "loading" } }));
    const cluster = workingClusters[ci];
    const cover = cluster.photos[cluster.coverIdx];
    const lat = cover.exif_data.latitude;
    const lng = cover.exif_data.longitude;
    const candidates = await findIdentifyCandidates(lat, lng);
    setIdentify((prev) => ({ ...prev, [ci]: { ...prev[ci], status: "done", candidates, pickedTitle: prev[ci]?.pickedTitle || matchingCandidate(currentName(ci), candidates) } }));
  };

  // Picking a result (or the city, or "None of these") is the newest choice, so it also replaces a name typed in the
  // place's box — a typed name wins over a pick, and picks seemed to do nothing (2026-11-02).
  const pickCandidate = (ci, title) => {
    setIdentify((prev) => ({ ...prev, [ci]: { ...prev[ci], pickedTitle: title } }));
    setWorkingClusters((prev) => prev.map((c, i) => (i === ci ? { ...c, typedName: title || "" } : c)));
  };
  // The name a place has before a result is picked: typed, else the photo's own name.
  const currentName = (ci) => {
    const c = workingClusters[ci];
    return c ? clusterBaseName({ typed: c.typedName, photo: c.photos[c.coverIdx] }) : "";
  };

  // Bulk "Identify Clusters" — see PhotoJournalReviewModal.js's identical
  // function for the full reasoning (sequential, not Promise.all; doesn't
  // auto-pick a candidate, just resolves each cluster's city). Only ever
  // touches workingClusters (new places), never workingMatches (already-
  // matched existing places, which keep their existing name/city).
  // Requested 2026-09-23.
  const [bulkIdentifying, setBulkIdentifying] = useState(false);
  const [bulkIdentifyProgress, setBulkIdentifyProgress] = useState(0);
  const identifyAllClusters = async () => {
    setBulkIdentifying(true);
    setBulkIdentifyProgress(0);
    for (let ci = 0; ci < workingClusters.length; ci++) {
      if (!identify[ci] || identify[ci].status !== "done") await runIdentify(ci, { skipCandidates: true });
      setBulkIdentifyProgress(ci + 1);
    }
    setBulkIdentifying(false);
  };

  // Final per-new-place name, in priority order: a picked Wikipedia
  // candidate > the typed base name (previewNames) > a resolved city (only
  // once Identify has run and found no pick) > the photo's own
  // filename/"New Place" default — same precedence as PhotoJournalReviewModal.
  // autoNames: what each place would be called without a typed name (the
  // box's grey hint); clusterNames: the final, unique names — a name typed
  // in the box wins (Common/placeNaming.js clusterBaseName, 4.10.55).
  const autoNames = useMemo(
    () =>
      workingClusters.map((cluster, ci) => {
        const id = identify[ci];
        return clusterBaseName({
          picked: id && id.pickedTitle,
          preview: previewNames ? previewNames[ci] : null,
          city: id && id.status === "done" ? id.city : null,
          photo: cluster.photos[cluster.coverIdx],
        });
      }),
    [identify, previewNames, workingClusters]
  );
  const clusterNames = useMemo(() => {
    const used = new Set((window.guide_obj?.places?.placesList || []).map((p) => (p.Name || "").toLowerCase()));
    return workingClusters.map((cluster, ci) =>
      Common.uniquePlaceName(clusterBaseName({ typed: cluster.typedName, picked: autoNames[ci] }), used)
    );
  }, [autoNames, workingClusters]);

  // A name typed in the place's box (they recognise it).
  const setTypedName = (ci, value) => {
    setWorkingClusters((prev) => prev.map((c, i) => (i === ci ? { ...c, typedName: value } : c)));
  };

  // Lets the user tell whether two new-place clusters are really the same
  // stop (e.g. a nearby town visited on an excursion) that should be
  // merged — see nearestOtherCluster's own comment.
  const nearestClusterInfo = useMemo(
    () => (workingClusters.length > 1 ? nearestOtherCluster(workingClusters, Common.haversineMeters) : []),
    [workingClusters]
  );

  // ── Existing-place match rows ──────────────────────────────────────────
  const toggleCover = (matchIdx, candidateIdx) => {
    setWorkingMatches((prev) => prev.map((m, i) => {
      if (i !== matchIdx) return m;
      return { ...m, coverIdx: m.coverIdx === candidateIdx ? null : candidateIdx };
    }));
  };

  const toggleNotes = (matchIdx, candidateIdx) => {
    setWorkingMatches((prev) => prev.map((m, i) => {
      if (i !== matchIdx) return m;
      const next = new Set(m.notesIncluded);
      if (next.has(candidateIdx)) next.delete(candidateIdx);
      else next.add(candidateIdx);
      return { ...m, notesIncluded: next };
    }));
  };

  // ── New-place cluster rows — same interactions as PhotoJournalReviewModal ──
  const toggleInclude = (clusterIdx, photoIdx) => {
    setWorkingClusters((prev) => prev.map((cluster, ci) => {
      if (ci !== clusterIdx) return cluster;
      if (photoIdx === cluster.coverIdx) return cluster;
      const next = new Set(cluster.includedIdxs);
      if (next.has(photoIdx)) next.delete(photoIdx);
      else next.add(photoIdx);
      return { ...cluster, includedIdxs: next };
    }));
  };

  const toggleClusterEnabled = (clusterIdx) => {
    setWorkingClusters((prev) => prev.map((c, i) => (i === clusterIdx ? { ...c, enabled: !c.enabled } : c)));
  };

  const setAllClustersEnabled = (enabled) => {
    setWorkingClusters((prev) => prev.map((c) => ({ ...c, enabled })));
  };

  const setClusterCover = (clusterIdx, photoIdx) => {
    setWorkingClusters((prev) => prev.map((cluster, ci) => {
      if (ci !== clusterIdx) return cluster;
      const next = new Set(cluster.includedIdxs);
      next.add(photoIdx);
      return { ...cluster, coverIdx: photoIdx, includedIdxs: next };
    }));
  };

  const splitOut = (clusterIdx, photoIdx) => {
    setWorkingClusters((prev) => {
      const cluster = prev[clusterIdx];
      const photo = cluster.photos[photoIdx];
      const remainingPhotos = cluster.photos.filter((_, i) => i !== photoIdx);
      const next = prev.filter((_, i) => i !== clusterIdx);
      if (remainingPhotos.length > 0) {
        const remainingIncluded = new Set(
          [...cluster.includedIdxs].filter((i) => i !== photoIdx).map((i) => (i > photoIdx ? i - 1 : i))
        );
        const newCoverIdx = cluster.coverIdx === photoIdx ? 0 : cluster.coverIdx > photoIdx ? cluster.coverIdx - 1 : cluster.coverIdx;
        next.splice(clusterIdx, 0, { photos: remainingPhotos, coverIdx: newCoverIdx, includedIdxs: remainingIncluded, enabled: cluster.enabled });
      }
      next.push({ photos: [photo], coverIdx: 0, includedIdxs: new Set([0]), enabled: true });
      return next;
    });
  };

  const mergeIntoPrevious = (clusterIdx) => {
    if (clusterIdx === 0) return;
    setWorkingClusters((prev) => {
      const target = prev[clusterIdx - 1];
      const source = prev[clusterIdx];
      const offset = target.photos.length;
      const mergedPhotos = [...target.photos, ...source.photos];
      const mergedIncluded = new Set([
        ...target.includedIdxs,
        ...[...source.includedIdxs].map((i) => i + offset),
      ]);
      const next = prev.filter((_, i) => i !== clusterIdx);
      next[clusterIdx - 1] = { photos: mergedPhotos, coverIdx: target.coverIdx, includedIdxs: mergedIncluded, typedName: target.typedName, enabled: target.enabled };
      return next;
    });
  };

  // asSearchResults: the new places go onto the map as search results (added
  // one by one with +) instead of straight into the guide (2026-10-19).
  const handleApply = (asSearchResults = false) => {
    const newPlaces = (onlyWebCovers ? [] : workingClusters)
      .map((cluster, ci) => ({ cluster, ci }))
      .filter(({ cluster }) => cluster.enabled)
      .map(({ cluster, ci }) => ({
        coverPhoto: cluster.photos[cluster.coverIdx],
        extraPhotos: cluster.photos.filter((_, i) => i !== cluster.coverIdx && cluster.includedIdxs.has(i)),
        // Only overrides the resulting place's name — the photo's own
        // filename (and the actual file, wherever it's stored) is untouched.
        // Only applies to new places, not photos matched to existing ones.
        // Uses the exact names already shown in the preview (previewNames),
        // already deduped against existing places — not recomputed here, so
        // apply can never disagree with what was previewed.
        placeName: clusterNames[ci],
      }));
    const deletePlaceIds = onlyWebCovers ? [...deleteIds] : [];
    const matches = workingMatches
      .filter((m) => (onlyWebCovers ? isWebPhoto(currentPhoto(m.place)) : !m.extra))
      .filter((m) => !deletePlaceIds.includes(m.place.ID))
      .map((m) => ({
        place: m.place,
        coverPhoto: m.coverIdx != null ? m.candidates[m.coverIdx].photo : null,
        notesCandidates: [...m.notesIncluded].map((i) => m.candidates[i]),
      }))
      .filter((m) => m.coverPhoto || m.notesCandidates.length > 0);
    onApply({ newPlaces, matches: onlyNew ? [] : matches, guideCoverPhoto, asSearchResults, deletePlaceIds });
  };

  const shownMatch = (m) => (onlyWebCovers ? isWebPhoto(currentPhoto(m.place)) : !m.extra);
  const totalCount = (onlyWebCovers ? 0 : workingClusters.length) + workingMatches.filter(shownMatch).length;
  const newCount = onlyWebCovers ? 0 : workingClusters.filter((c) => c.enabled).length;
  const webMatchCount = workingMatches.filter((m) => isWebPhoto(currentPhoto(m.place))).length;
  const deleteCount = onlyWebCovers ? deleteIds.size : 0;
  const applyCount = newCount + deleteCount
    + (onlyNew ? 0 : workingMatches.filter((m) => shownMatch(m) && !(onlyWebCovers && deleteIds.has(m.place.ID)) && (m.coverIdx != null || m.notesIncluded.size > 0)).length);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      {mapPick ? (
        <MapPickDialog
          show
          lat={mapPick.lat}
          lng={mapPick.lng}
          onHide={() => setMapPick(null)}
          onPick={(name) => setTypedName(mapPick.ci, name)}
        />
      ) : null}
      {viewerPhoto ? (
        <PhotoViewer
          photos={[{ src: resolveUrl(viewerPhoto.url || viewerPhoto.url_m, viewerPhoto.thumb), name: photoName(viewerPhoto) }]}
          index={0}
          onClose={() => setViewerPhoto(null)}
        />
      ) : null}
      <Modal.Header closeButton style={{ padding: "12px 16px" }}>
        <div style={{ width: "100%" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
            Personalize Guide with Photos — {totalCount} place{totalCount !== 1 ? "s" : ""}
          </Modal.Title>
          {/* Fixed header, not the scrollable body — see PhotoJournalReviewModal.js's
              matching comment for why. Found 2026-09-24. */}
          {bulkIdentifying && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <FontAwesomeIcon icon={solid("spinner")} spin style={{ color: "#6b7280", fontSize: 12 }} />
              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                Identifying clusters… {bulkIdentifyProgress}/{workingClusters.length}
              </span>
              <div style={{ flex: 1, height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${workingClusters.length ? (bulkIdentifyProgress / workingClusters.length) * 100 : 0}%`,
                    background: "#6366f1", transition: "width 0.2s",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "8px 16px" }}>
        <p style={{ fontSize: "0.8rem", color: "#666", margin: "0 0 8px" }}>
          Photos near an existing place can become its ★ cover photo and/or be added to its ☑ notes. Photos not near
          any place are grouped into new places below — uncheck a photo to leave it out, ✂ to split it into its own
          place, or merge a stray one into the place above it. 🏠 on any photo makes it the guide's own cover photo.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>Match/group photos within</label>
          <Form.Range
            min={0}
            max={1000}
            step={10}
            value={sensitivity}
            onChange={(e) => onSensitivityChange(parseInt(e.target.value, 10))}
            style={{ maxWidth: 200 }}
          />
          <span style={{ fontSize: "0.8rem", color: "#374151", minWidth: 60 }}>
            {sensitivity >= 1000 ? `${(sensitivity / 1000).toFixed(1)} km` : `${sensitivity} m`}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>
            Only suggest new places at least
          </label>
          <Form.Range
            min={0}
            max={50000}
            step={1000}
            value={minNewPlaceDistance}
            onChange={(e) => onMinNewPlaceDistanceChange(parseInt(e.target.value, 10))}
            style={{ maxWidth: 200 }}
          />
          <span style={{ fontSize: "0.8rem", color: "#374151", minWidth: 90 }}>
            {minNewPlaceDistance > 0 ? `${(minNewPlaceDistance / 1000).toFixed(0)} km away` : "off (any distance)"}
          </span>
        </div>

        {workingClusters.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>
              But limit a new place to
            </label>
            <Form.Range
              // Was floored at 200 — the sensitivity slider right above this
              // one can go down to 0, so tightening sensitivity below ~200m
              // (a museum, a small historic center) made this cap
              // unreachable and effectively a no-op, since natural clusters
              // rarely spanned past 200m anyway once already join-limited
              // that tight. Found 2026-09-14.
              min={50}
              max={5000}
              step={50}
              value={maxSpan}
              onChange={(e) => onMaxSpanChange(parseInt(e.target.value, 10))}
              style={{ maxWidth: 200 }}
            />
            <span style={{ fontSize: "0.8rem", color: "#374151", minWidth: 60 }}>
              {maxSpan >= 1000 ? `${(maxSpan / 1000).toFixed(1)} km` : `${maxSpan} m`} across
            </span>
          </div>
        )}

        {unmatchedPlaces && unmatchedPlaces.length > 0 && (
          <div style={{ marginBottom: 14, fontSize: "0.8rem" }}>
            <span style={{ color: "#b45309" }}>
              ⚠ {unmatchedPlaces.length} existing place{unmatchedPlaces.length !== 1 ? "s" : ""} won't get a photo at this distance
            </span>
            {" — "}
            <span
              style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setShowUnmatched((v) => !v)}
            >
              {showUnmatched ? "hide" : "show"}
            </span>
            {showUnmatched && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 20, color: "#374151" }}>
                {unmatchedPlaces.map((place) => (
                  <li key={place.ID}>
                    {place.Name}
                    {!place.Photo && !place.Photo_m && (
                      <span style={{ color: "#b45309" }}> — no photo at all</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {workingClusters.length > 0 && workingMatches.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <input
              type="checkbox"
              id="personalize-only-new"
              checked={onlyNew}
              onChange={(e) => setOnlyNew(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <label htmlFor="personalize-only-new" style={{ fontSize: "0.8rem", color: "#374151", cursor: "pointer" }}>
              Only add new places — leave the {workingMatches.length} existing place{workingMatches.length !== 1 ? "s'" : "'s"} photos as they are
            </label>
          </div>
        )}

        {webMatchCount > 0 && !onlyNew && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <input
              type="checkbox"
              id="personalize-only-web"
              checked={onlyWebCovers}
              onChange={(e) => setOnlyWebCovers(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <label htmlFor="personalize-only-web" style={{ fontSize: "0.8rem", color: "#374151", cursor: "pointer" }}>
              Only places whose photo is from the web ({webMatchCount}) and your photos near them (within 1 km) — everything else is
              hidden and left as it is
            </label>
          </div>
        )}

        {workingClusters.length > 0 && !onlyWebCovers && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <input
              type="checkbox"
              id="personalize-select-all-new"
              checked={workingClusters.every((c) => c.enabled)}
              onChange={(e) => setAllClustersEnabled(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <label htmlFor="personalize-select-all-new" style={{ fontSize: "0.8rem", color: "#374151", cursor: "pointer" }}>
              Add all new places below ({workingClusters.filter((c) => c.enabled).length}/{workingClusters.length})
            </label>
          </div>
        )}

        {workingClusters.length > 0 && !onlyWebCovers && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>
              Name new places <span style={{ color: "#9ca3af" }}>(new places only, below)</span>
            </label>
            <input
              type="text"
              value={baseName}
              onChange={(e) => setBaseName(e.target.value)}
              placeholder="e.g. Japan (optional)"
              style={{
                fontSize: "0.8rem", padding: "4px 8px", borderRadius: 6,
                border: "1px solid #d1d5db", maxWidth: 200,
              }}
            />
            {previewNames && previewNames.length > 0 && (
              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                → "{previewNames[0]}"{previewNames.length > 1 ? `, "${previewNames[1]}", …` : ""}
              </span>
            )}
            <button
              onClick={identifyAllClusters}
              disabled={bulkIdentifying}
              title="Reverse-geocode + Wikipedia-search every new-place cluster below — a billed Google Geocoding call per cluster"
              style={{
                marginLeft: "auto", border: "1px solid #d1d5db", borderRadius: 6,
                background: "#fff", color: "#374151", fontSize: "0.75rem", padding: "4px 10px",
                cursor: bulkIdentifying ? "default" : "pointer", whiteSpace: "nowrap",
              }}
            >
              {bulkIdentifying ? `Identifying… ${bulkIdentifyProgress}/${workingClusters.length}` : "🔎 Identify Clusters"}
            </button>
          </div>
        )}

        {workingMatches.map((m, mi) => (!shownMatch(m) ? null : (
          <div key={m.place.ID} className="pgr-match" style={{ padding: "10px 4px", borderBottom: "1px solid #f0f0f0", ...(onlyNew ? { opacity: 0.4, pointerEvents: "none" } : {}) }}>
            <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", textDecoration: deleteIds.has(m.place.ID) && onlyWebCovers ? "line-through" : "none" }}>{m.place.Name}</span>
              {m.place.City && (
                <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>{m.place.City}</span>
              )}
              {onlyWebCovers ? (
                <label className="pgr-delete" style={{ marginLeft: "auto", fontSize: 12, color: "#b91c1c", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }} title="You didn't go here: remove the place from the guide">
                  <input type="checkbox" checked={deleteIds.has(m.place.ID)} onChange={() => toggleDelete(m.place.ID)} style={{ cursor: "pointer" }} />
                  Delete this place
                </label>
              ) : null}
            </div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              <CurrentThumb url={currentPhoto(m.place)} onEnlarge={() => setViewerPhoto({ url: currentPhoto(m.place), name: m.place.Name })} />
              {m.candidates.length === 0 ? (
                <div style={{ alignSelf: "center", fontSize: 12, color: "#9ca3af" }}>None of your photos within 1 km</div>
              ) : null}
              {m.candidates.map((c, ci) => (
                <MatchThumb
                  key={ci}
                  onEnlarge={() => setViewerPhoto(c.photo)}
                  src={resolveUrl(c.photo.url_m || c.photo.url, c.photo.thumb)}
                  name={photoName(c.photo)}
                  dist={c.dist}
                  isCover={m.coverIdx === ci}
                  inNotes={m.notesIncluded.has(ci)}
                  isGuideCover={guideCoverPhoto === c.photo}
                  onToggleCover={() => toggleCover(mi, ci)}
                  onToggleNotes={() => toggleNotes(mi, ci)}
                  onToggleGuideCover={() => toggleGuideCover(c.photo)}
                />
              ))}
            </div>
          </div>
        )))}

        {!onlyWebCovers && workingClusters.map((cluster, ci) => (
          <div key={`new-${ci}`} style={{ padding: "10px 4px", borderBottom: "1px solid #f0f0f0", opacity: cluster.enabled ? 1 : 0.5 }}>
            <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={cluster.enabled}
                onChange={() => toggleClusterEnabled(ci)}
                title="Add this place"
                style={{ marginRight: 8, cursor: "pointer" }}
              />
              <span style={{
                fontSize: 10, fontWeight: 700, color: "#fff", background: "#16a34a",
                borderRadius: 4, padding: "2px 6px", marginRight: 8, letterSpacing: 0.3,
              }}>
                NEW
              </span>
              <input
                className="pjr-name-input"
                value={cluster.typedName || ""}
                placeholder={autoNames[ci]}
                onChange={(e) => setTypedName(ci, e.target.value)}
                aria-label={`Name for ${autoNames[ci]}`}
                title="Type a name if you recognise this place"
                style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", border: "1px solid #d1d5db", borderRadius: 6, padding: "3px 8px", minWidth: 0, width: 240, maxWidth: "50%" }}
              />
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>
                {cluster.includedIdxs.size} photo{cluster.includedIdxs.size !== 1 ? "s" : ""}
              </span>
              {nearestClusterInfo[ci] && (
                <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>
                  · nearest: {clusterNames[nearestClusterInfo[ci].index]} ({formatDist(nearestClusterInfo[ci].dist)})
                </span>
              )}
              {ci > 0 && (
                <button
                  onClick={() => mergeIntoPrevious(ci)}
                  title="Merge into the place above"
                  style={{
                    marginLeft: "auto", border: "1px solid #d1d5db", borderRadius: 6,
                    background: "#fff", color: "#374151", fontSize: 11, padding: "3px 8px", cursor: "pointer",
                  }}
                >
                  ⤴ Merge into previous
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {cluster.photos.map((photo, pi) => (
                <NewPlaceThumb
                  key={pi}
                  onEnlarge={() => setViewerPhoto(photo)}
                  src={resolveUrl(photo.url_m || photo.url, photo.thumb)}
                  name={photoName(photo)}
                  included={cluster.includedIdxs.has(pi)}
                  isCover={pi === cluster.coverIdx}
                  isGuideCover={guideCoverPhoto === photo}
                  onToggleInclude={() => toggleInclude(ci, pi)}
                  onSetCover={() => setClusterCover(ci, pi)}
                  onSplitOut={() => splitOut(ci, pi)}
                  onToggleGuideCover={() => toggleGuideCover(photo)}
                  showSplit={cluster.photos.length > 1}
                />
              ))}
            </div>
            <IdentifyPlace
              state={identify[ci]}
              onRun={() => runIdentify(ci)}
              onFindCandidates={() => findCandidatesOnly(ci)}
              onPick={(title) => pickCandidate(ci, title)}
              onPickOnMap={() => {
                const cover = workingClusters[ci].photos[workingClusters[ci].coverIdx];
                setMapPick({ ci, lat: cover.exif_data.latitude, lng: cover.exif_data.longitude });
              }}
            />
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer style={{ padding: "10px 16px", gap: 8 }}>
        {newCount > 0 && (
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, marginRight: "auto", fontSize: 13, cursor: "pointer" }}
            title="Show the new places on the map as search results, to look at and add one by one with +"
          >
            <input type="checkbox" checked={asResults} onChange={(e) => setAsResults(e.target.checked)} />
            Show new places as search results
          </label>
        )}
        <button className="btn btn-secondary btn-sm" onClick={onHide}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={() => handleApply(newCount > 0 && asResults)} disabled={applyCount === 0}>
          Apply to {applyCount} place{applyCount !== 1 ? "s" : ""}
          {deleteCount > 0 ? ` (${deleteCount} deleted)` : ""}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
